/**
 * Nextjs implementation of component builder class for building components based on the configuration.
 */
export class ComponentBuilder {
    constructor(config) {
        this.config = config;
        /**
         * SXA uses custom default export name
         */
        this.DEFAULT_EXPORT_NAME = 'Default';
        this.components = new Map([...config.components]);
    }
    /**
     * Creates a new instance of module factory
     * Module factory provides a module (file) including all exports.
     * Module can be imported dynamically or statically.
     * @returns {ModuleFactory} Module factory implementation
     */
    getModuleFactory() {
        return (componentName) => {
            const component = this.components.get(componentName);
            if (!component)
                return null;
            // check if module should be imported dynamically
            if (component.module) {
                return component.module();
            }
            return component;
        };
    }
    /**
     * Creates a new instance of component factory
     * Component can be imported dynamically or statically.
     * @param {object} [config] Component factory configuration
     * @param {boolean} [config.isEditing] Indicates if component factory is used in editing mode
     * @returns {ComponentFactory} Component factory implementation
     */
    getComponentFactory({ isEditing } = {}) {
        return (componentName, exportName) => {
            const component = this.components.get(componentName);
            if (!component)
                return null;
            // check if component should be imported dynamically
            if (component.element) {
                // Editing mode doesn't work well with dynamic components in nextjs: dynamic components are not displayed without refresh after a rendering is added.
                // This happens beacuse Sitecore editors simply insert updated HTML generated on server side. This conflicts with nextjs dynamic logic as no HTML gets rendered for dynamic component
                // So we use require() to obtain dynamic components in editing mode while preserving dynamic logic for non-editing scenarios
                // As we need to be able to seamlessly work with dynamic components in both editing and normal modes, different componentFactory functions will be passed to app
                return component.element(isEditing);
            }
            if (exportName && exportName !== this.DEFAULT_EXPORT_NAME) {
                return component[exportName];
            }
            return (component.Default ||
                component.default ||
                component);
        };
    }
}
